import { useState, useCallback } from "react";

/**
 * A custom React hook for managing history (undo) state.
 * Supports any state structure (objects, arrays, primitives).
 *
 * @param {any} initialState - The starting state
 * @param {number} [maxDepth=50] - Maximum undo history size
 * @returns {[any, Function, Function, boolean, Function]} - [state, setState, undo, canUndo, resetHistory]
 */
export const useUndo = (initialState, maxDepth = 50) => {
  const [state, _setState] = useState(initialState);
  const [history, setHistory] = useState([]);

  // Sets state and records the current state to history.
  // Set shouldRecord=false to make a state change without pushing to history.
  const setState = useCallback((nextState, shouldRecord = true) => {
    _setState((current) => {
      const resolved = typeof nextState === "function" ? nextState(current) : nextState;
      if (shouldRecord) {
        setHistory((prev) => {
          // Deep clone the current state to prevent reference mutations in history
          const clonedCurrent = JSON.parse(JSON.stringify(current));
          const nextStack = [...prev, clonedCurrent];
          if (nextStack.length > maxDepth) {
            nextStack.shift();
          }
          return nextStack;
        });
      }
      return resolved;
    });
  }, [maxDepth]);

  // Restores the previous state from history. Returns the remaining history length.
  const undo = useCallback(() => {
    let remaining = 0;
    setHistory((prev) => {
      if (prev.length === 0) return prev;
      const prevState = prev[prev.length - 1];
      _setState(prevState);
      remaining = prev.length - 1;
      return prev.slice(0, -1);
    });
    return remaining;
  }, []);

  // Wipes history and sets a fresh state. Useful after saving or loading new data.
  // Pass null to only clear the history stack without changing the current state.
  const resetHistory = useCallback((newState) => {
    if (newState !== null) {
      _setState(newState);
    }
    setHistory([]);
  }, []);

  const canUndo = history.length > 0;

  return [state, setState, undo, canUndo, resetHistory];
};

export default useUndo;
